import React from 'react';
import ProgressRing from './ProgressRing';

const DOMAIN_COLORS = {
    '1.0': { ring: '#6366f1', bg: 'bg-indigo-50',   border: 'border-indigo-200',  text: 'text-indigo-700',  activeBg: 'bg-indigo-100'  },
    '2.0': { ring: '#0ea5e9', bg: 'bg-sky-50',       border: 'border-sky-200',     text: 'text-sky-700',     activeBg: 'bg-sky-100'     },
    '3.0': { ring: '#10b981', bg: 'bg-emerald-50',   border: 'border-emerald-200', text: 'text-emerald-700', activeBg: 'bg-emerald-100' },
    '4.0': { ring: '#8b5cf6', bg: 'bg-violet-50',    border: 'border-violet-200',  text: 'text-violet-700',  activeBg: 'bg-violet-100'  },
    '5.0': { ring: '#f59e0b', bg: 'bg-amber-50',     border: 'border-amber-200',   text: 'text-amber-700',   activeBg: 'bg-amber-100'   },
    '6.0': { ring: '#f43f5e', bg: 'bg-rose-50',      border: 'border-rose-200',    text: 'text-rose-700',    activeBg: 'bg-rose-100'    },
    '7.0': { ring: '#14b8a6', bg: 'bg-teal-50',      border: 'border-teal-200',    text: 'text-teal-700',    activeBg: 'bg-teal-100'    },
};

const DEFAULT_COLOR = { ring: '#D67C2C', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', activeBg: 'bg-orange-100' };

const MATURITY_SHORT = { 1: 'Inceptive', 2: 'Transient', 3: 'Stable', 4: 'Dynamic' };

/**
 * DomainScoreCard — compact card with circular progress ring.
 *
 * Props:
 *   domainId      - string (e.g. "1.0")
 *   domainName    - string
 *   score         - 0–100 (weighted percentage)
 *   averageLevel  - 0–4 float
 *   actionCount   - number of action items
 *   isActive      - bool (currently selected tab)
 *   onClick       - () => void
 */
const DomainScoreCard = ({
    domainId,
    domainName,
    score = 0,
    averageLevel = 0,
    actionCount = 0,
    isActive = false,
    onClick,
}) => {
    const colors = DOMAIN_COLORS[domainId] || DEFAULT_COLOR;
    const maturityLevel = Math.max(1, Math.min(4, Math.round(averageLevel)));
    const maturityLabel = MATURITY_SHORT[maturityLevel] || 'Inceptive';

    // Ring color based on score
    const ringColor = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';

    // Abbreviate domain name to 2 words
    const shortName = domainName.split(' ').slice(0, 2).join(' ');

    return (
        <button
            onClick={onClick}
            className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all cursor-pointer min-w-[110px] ${
                isActive
                    ? `${colors.activeBg} ${colors.border} shadow-md`
                    : `bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm`
            }`}
        >
            <ProgressRing
                percent={score}
                size={64}
                stroke={6}
                color={ringColor}
                trackColor="#f1f5f9"
                label={`${Math.round(score)}%`}
                sublabel={maturityLabel}
            />
            <p className={`text-xs font-semibold text-center leading-tight max-w-[90px] ${isActive ? colors.text : 'text-gray-600'}`}>
                {shortName}
            </p>
            {actionCount > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isActive ? `${colors.bg} ${colors.text}` : 'bg-gray-50 text-gray-400'}`}>
                    {actionCount} actions
                </span>
            )}
        </button>
    );
};

export default DomainScoreCard;
export { DOMAIN_COLORS };
